<template>
  <!------------------------------------------
    Smaller state map on the print version of the congressional state profiles.
  -------------------------------------------->
  <div id="congr-district-inset-print-map">
    <div>
      <MapTemplate :showLayerPanel="false" :mapContainerId="mapContainerId" :loading="loading" :mapHeight="mapHeight" />
    </div>
  </div>
</template>

<script>
import MapTemplate from '@/components/map/MapTemplate'
import mapboxgl from "mapbox-gl"
import states from '@/js/stFips.js'
import bboxes from '@/js/bboxes.js'

let map

export default {
  name: 'CDProfilesPrintStateMap',

  components: { MapTemplate },

  props: {
    selectedCDFips: { type: String, required: true },
    colorPerServArea: { type: Array, required: true },
    officesPerGrantee: { type: Object, required: true },
    mapboxStyle: { type: String, required: false, default: "mapbox://styles/lsc-odga/cl8sv2oq8003i15p90nywwsu6?optimize=true" },
    interactive: { type: Boolean, required: false, default: true },
    mapHeight: { type: String, required: false, default: '8rem' }
  },

  data () {
    return {
      loaded: false,
      newBBox: null, // When changing the bounding boxes, set the new bounding box to be newBBox to automatically re-fit the map,
      // canvasBounds: null,
      defaultBoundsPadding: 5,
      accessToken: `${process.env.VUE_APP_MAPBOX_TOKEN}`,
      map: null,
      defaultBBox: [-124.736342, 24.521208, -66.945392, 49.382808], // contiguous US (used if no bbox provided)
      allowScrollZoom: false,
      loading: false,
      mapContainerId: 'map-container-st',
      layers: [
        { 
          tilesetId: 'lsc-odga.2021_states',
          loadOnStart: true,
          filterOn: 'st_abbrev',
          layerDefs: {
            'id': '2021_states-fill',
            'type': 'fill',
            'source': `2021_states-fill-src`,
            'source-layer': '2021_states',
            'paint': {
              'fill-color': '#eee',
              'fill-outline-color': '#bbb'
            }
          }
        },
        { 
          tilesetId: 'lsc-odga.2024_bf_general_v2',
          loadOnStart: true,
          filterOn: 'st_abbrev',
          layerDefs: {
            'id': '2024_bf_general_v2-fill',
            'type': 'fill',
            'source': `2024_bf_general_v2-fill-src`,
            'source-layer': '2024_bf_general_v2',
            'paint': {
              'fill-opacity': 0.7,
              'fill-color': '#165991',
              'fill-outline-color': 'rgba(0,0,0,0)'
            }
          }
        },
        { 
          tilesetId: 'lsc-odga.119th_congress',
          loadOnStart: true,
          filterOn: 'geoid',
          layerDefs: {
            'id': '119th_congress-fill',
            'type': 'fill',
            'source': `119th_congress-fill-src`,
            'source-layer': '119th_congress',
            'paint': {
              'fill-opacity': 0,
              'fill-color': '#f6f6f6',
            }
          }
        },
        { 
          tilesetId: 'lsc-odga.119th_congress',
          loadOnStart: true,
          filterOn: 'geoid',
          layerDefs: {
            'id': '119th_congress',
            'type': 'line',
            'source': `119th_congress-src`,
            'source-layer': '119th_congress',
            'paint': {
              'line-width': 1.5,
              'line-color': 'black'
            }
          }
        },
      ]
    }
  },

  computed: {
    bbox () { return this.newBBox === null || this.newBBox === undefined ? this.defaultBBox : this.newBBox },
    serviceAreaIds() { return this.colorPerServArea.map(x => x.area) },
    paddingOrDefault () { return this.padding === null || this.padding === undefined ? this.defaultBoundsPadding : this.padding },
    stateFips () {
      return this.selectedCDFips.substring(0,2)
    },
    stateAbbrev () {
      return states.filter(x => x.fips === this.stateFips)[0].st
    },
    filterCDField () { 
      return this.selectedCDFips !== '' ? ["==", "geoid", this.selectedCDFips] : ['has', 'placeholder'] 
    },
    filterStateAbbrev () { 
      return this.selectedCDFips !== '' ? ["==", "st_abbrev", this.stateAbbrev] : ['has', 'placeholder'] 
    },
    servAreaColorExpression () {
      let exp = ['match', ['get', 'serv_area']]
      this.colorPerServArea.forEach(function (d) {
        exp.push(
          d['area'],
          d['color']
        )
      })
      exp.push("rgba(0,0,0,0)") // default if no match
      return exp
    }
  },

  created () {
  },
  
  async mounted () {
    // Initialize the map
    mapboxgl.accessToken = this.accessToken
    this.$nextTick(() => {
      this.mapSetup()
    })

    if (this.selectedCDFips && this.serviceAreaIds.length > 0) {
      this.newBBox = bboxes.states[this.stateFips]
    }
  },

  methods: {
    mapSetup: function() { 
      this.loading = true
      let padding = this.paddingOrDefault,
          allowScrollZoom = this.allowScrollZoom,
          mapContainerId = this.mapContainerId,
          component = this

      map = new mapboxgl.Map({
        container: mapContainerId,
        style: this.mapboxStyle,
        interactive: this.interactive,
        preserveDrawingBuffer: true
      })

      map.fitBounds( component.bbox, { padding: padding, duration:0 })

      if (allowScrollZoom === false) {
        map.scrollZoom.disable()
      }

      // ZOOM CONTROLS
      map.addControl(new mapboxgl.FullscreenControl({container: document.querySelector('#' + mapContainerId)}), 'top-left')
      const nav = new mapboxgl.NavigationControl({
        visualizePitch: true
      })
      map.addControl(nav, 'top-left')

      // LAYERS
      map.on('load', function () {
        if (component.layers.length > 0) {
          component.initLayers()
        }

        setTimeout(() => { 
          component.loading = false 
          component.loaded = true
        }, 100)
      })
    },

    async initLayers () {
      this.layers.forEach(layer => {
        if (layer.loadOnStart === true && map.getSource(layer.layerDefs['source']) === undefined) {
          map.addSource(`${layer.layerDefs['source']}`, {
            type: 'vector',
            url: `mapbox://${layer.tilesetId}`
          })
          map.addLayer(layer.layerDefs)
        }
      })
      this.filterLayers()
    },

    fitBounds (bbox) {
      if (map) {
        let padding = this.paddingOrDefault
        map.fitBounds( bbox, { padding: padding, duration:0 })
      }
    },

    filterLayers () {
      const servAreaColorExp = this.servAreaColorExpression
      if (map) {
        this.layers.forEach(layer => {
          try {
            // FILTER
            if (layer.filterOn === 'geoid') {
              layer.layerDefs['filter'] = this.filterCDField
              map.setFilter(layer.layerDefs['id'], this.filterCDField)
            }
            if (layer.filterOn === 'st_abbrev') {
              layer.layerDefs['filter'] = this.filterStateAbbrev
              map.setFilter(layer.layerDefs['id'], this.filterStateAbbrev)
            }
            // COLOR
            if (layer.layerDefs['id'] === '2024_bf_general_v2-fill' && this.colorPerServArea.length > 0) {
              map.setPaintProperty(layer.layerDefs['id'], 'fill-color', servAreaColorExp)
            }
          } catch (err) {
            console.log(err)
          }
        })
      }
    },

    hideMapElementsForPrint () {
      const elements = document.querySelectorAll('.mapboxgl-control-container');
      elements.forEach(el => { el.remove() })
    }
  },

  watch: {
    newBBox(to) {
      this.fitBounds(to)
    },
    loaded (to) {
      if (to === true) {
        this.hideMapElementsForPrint()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/vars.scss';
.mapboxgl-canvas {
  border-radius: 1rem;
}
</style>